import { registerIVSQualityPlugin, registerIVSTech } from 'amazon-ivs-player'
import PropTypes from 'prop-types'
import React, { useEffect, useReducer, useState } from 'react'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import {
  LIVE,
  RECONNECT,
  RESET
} from '../../../constants/actions/videoPlayerActions'
import {
  initialState,
  videoPlayerReducer
} from '../../../reducers/videoPlayerReducer'
import HighlightedPM from './HighlightedPM'
import NotificationBar from './NotificationBar'
import StreamOffline from './StreamOffline'
import './vjs-moal.css'

registerIVSTech(videojs, {
  wasmWorker: '/amazon-ivs-wasmworker.min.js',
  wasmBinary: '/amazon-ivs-wasmworker.min.wasm'
})
registerIVSQualityPlugin(videojs)

const StreamOnline = ({ IVSPlaybackURL }) => {
  const [state, dispatch] = useReducer(videoPlayerReducer, initialState)

  const videoRef = React.useRef(null)
  const playerRef = React.useRef(null)

  const [muted, setMuted] = useState(false)
  const [browserBlocked, setBrowserblocked] = useState(false)

  let updater // uses a 5 second interval to check stream status

  useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
      const videoElement = document.createElement('video-js')
      videoRef.current.appendChild(videoElement)
      const player = (playerRef.current = videojs(
        videoElement,
        {
          techOrder: ['AmazonIVS'],
          autoplay: true,
          controls: true,
          // responsive: true,
          fill: true,
          playsinline: true
        },
        () => {
          // Play stream
          player.enableIVSQualityPlugin()
          player.src(IVSPlaybackURL)
          player.addClass('vjs-moal')
        }
      ))
    }
  }, [videoRef])

  useEffect(() => {
    if (state.reconnectAttempts > 0 && state.reconnectAttempts <= 5) {
      if (state.reconnecting === true && state.live === false) {
        updater = setTimeout(() => {
          playerRef.current.src(IVSPlaybackURL)
        }, 5000)
      }
    }

    return () => {
      clearTimeout(updater)
    }
  }, [state.reconnectAttempts])

  const handleReconnectAttempt = () => {
    dispatch({ type: RESET })
  }

  // const handleBuffering = () => {
  //   dispatch({ type: LIVE, payload: true })
  //   console.log('handleBuffering')
  // }

  const handleEnded = () => {
    dispatch({ type: RESET })
    // console.log('handleEnded')
  }

  // const handleIdle = () => {
  //   dispatch({ type: LIVE, payload: true })
  //   console.log('handleIdle')
  // }

  const handlePlaying = () => {
    dispatch({ type: LIVE, payload: true })
    unmuteStream()
  }

  // const handleReady = () => {
  //   dispatch({ type: LIVE, payload: true })
  //   console.log('handleReady')
  // }

  // const handlePlayerError = (error) => {
  //   console.log('handlePlayerError')
  //   console.log(error)
  // }

  const unmuteStream = () => {
    if (playerRef.current.muted()) {
      // Create the button element
      var button = document.createElement('button')

      // Set the button text
      button.innerHTML = 'Click Me'

      // Add an onclick event handler
      button.onclick = function () {
        playerRef.current.muted(false)
      }

      // Emulate a user clicking the button
      button.click()
    }
  }

  const handleAudioBlocked = () => {
    setBrowserblocked(true)
  }

  const handlePlaybackBlocked = () => {
    dispatch({ type: LIVE, payload: true })
  }

  const handleIVSError = (error) => {
    console.log('handleIVSError')
    console.log(error)
    dispatch({ type: RECONNECT, payload: { reconnecting: true } })
  }

  const handleVolumeChange = () => {
    if (playerRef.current.muted()) {
      setMuted(true)
    } else {
      setMuted(false)
    }
    // TODO: Check for Browser Tab Mute, not just videojs mute control
  }

  // Dispose the Video.js player when the functional component unmounts
  useEffect(() => {
    const player = playerRef.current
    const IVSPlayer = player.getIVSPlayer()
    const PlayerState = player.getIVSEvents().PlayerState
    const PlayerEvent = player.getIVSEvents().PlayerEventType

    player.on('volumechange', handleVolumeChange)
    // IVSPlayer.addEventListener(PlayerState.READY, handleReady)
    // IVSPlayer.addEventListener(PlayerState.IDLE, handleIdle)
    IVSPlayer.addEventListener(PlayerState.PLAYING, handlePlaying)
    // IVSPlayer.addEventListener(PlayerState.BUFFERING, handleBuffering)
    IVSPlayer.addEventListener(PlayerState.ENDED, handleEnded)
    IVSPlayer.addEventListener(PlayerEvent.ERROR, handleIVSError)

    IVSPlayer.addEventListener(PlayerEvent.AUDIO_BLOCKED, handleAudioBlocked)
    IVSPlayer.addEventListener(
      PlayerEvent.PLAYBACK_BLOCKED,
      handlePlaybackBlocked
    )

    // player.on('error', handlePlayerError)

    return () => {
      // IVSPlayer.removeEventListener(PlayerState.READY, handleReady)
      // IVSPlayer.removeEventListener(PlayerState.IDLE, handleIdle)
      IVSPlayer.removeEventListener(PlayerState.PLAYING, handlePlaying)
      // IVSPlayer.removeEventListener(PlayerState.BUFFERING, handleBuffering)
      IVSPlayer.removeEventListener(PlayerState.ENDED, handleEnded)
      IVSPlayer.removeEventListener(PlayerEvent.ERROR, handleIVSError)

      IVSPlayer.removeEventListener(
        PlayerEvent.AUDIO_BLOCKED,
        handleAudioBlocked
      )
      IVSPlayer.removeEventListener(
        PlayerEvent.PLAYBACK_BLOCKED,
        handlePlaybackBlocked
      )

      if (player && !player.isDisposed()) {
        player.dispose()
        playerRef.current = null
      }
    }
  }, [playerRef])

  return (
    <div className="w-full h-full">
      <div className="relative w-full h-full bg-black">
        {!state.live && state.reconnectAttempts > 5 && (
          <NotificationBar
            message="Error Loading Stream"
            type="error"
            cta={'Reload Stream'}
            onClick={handleReconnectAttempt}
          />
        )}
        {!state.live && (
          <StreamOffline
            text={
              !state.live && state.reconnectAttempts <= 5
                ? 'Loading...'
                : 'Stream failed to connect.'
            }
            link=""
            withWrapper
          />
        )}
        {state.live && muted && (
          <NotificationBar
            message="Warning: Audio is Muted"
            type="warning"
            cta={'Unmute'}
            onClick={() => {
              playerRef.current.muted(false)
              setMuted(false)
            }}
          />
        )}
        {state.live && browserBlocked && (
          <NotificationBar
            message="Autoplay of audio/video is blocked by your browser."
            type="warning"
            cta={'Help'}
            onClick={() => {
              window.open(
                '/audio-video-troubleshooting',
                '_blank',
                'noreferrer'
              )
            }}
          />
        )}
        <HighlightedPM />
        <div
          className={`${state.live ? 'aspect-video resize-helper' : 'hidden'}`}
          ref={videoRef}
        />
      </div>
    </div>
  )
}

StreamOnline.propTypes = {
  IVSPlaybackURL: PropTypes.string
}

export default StreamOnline
